// =========================================================================
// TRADE HISTORY MODAL - FULL 3-STAGE VIEW + IMPROVED EDIT
// =========================================================================
import React, { useState, useEffect } from 'react';
import { isSessionShifted, getEnteredCalendarDate } from '../lib/format';
import { C } from '../lib/colors';

export function TradeHistoryModal({ 
  trade, 
  isOpen, 
  onClose, 
  updateTrade, 
  customStrategies = [],
  accounts = [] 
}) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedForm, setEditedForm] = useState(null);

  useEffect(() => {
    if (trade) {
      setEditedForm({ ...trade });
    }
  }, [trade]);

  if (!isOpen || !trade) return null;

  const handleSave = async () => {
    if (editedForm) {
      await updateTrade(trade.id, editedForm);
    }
    setIsEditMode(false);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{trade.symbol} — {trade.direction} Trade</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {!isEditMode ? (
            <TradeInfoReadOnly trade={trade} onEdit={() => setIsEditMode(true)} />
          ) : (
            <TradeEditForm 
              editedForm={editedForm} 
              setEditedForm={setEditedForm} 
              onSave={handleSave} 
              onCancel={() => setIsEditMode(false)}
              customStrategies={customStrategies}
              accounts={accounts}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// READ-ONLY VIEW - Shows All Entered Data
// =========================================================================
function TradeInfoReadOnly({ trade, onEdit }) {
  const formatValue = (value) => value === null || value === undefined || value === '' ? '—' : value;

  return (
    <div>
      {/* 1. PRE-TRADE */}
      <section style={{ marginBottom: 24 }}>
        <h3>1. Pre-Trade</h3>
        <div className="fos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13 }}>
          <div><strong>Market Condition:</strong> {formatValue(trade.market_condition)}</div>
          <div><strong>Entry Signal:</strong> {formatValue(trade.entry_signal)}</div>
          <div><strong>Conviction Level:</strong> {formatValue(trade.conviction_level)}/10</div>
          <div><strong>Focus Level:</strong> {formatValue(trade.focus_level)}/10</div>
          <div><strong>Rule Adherence:</strong> {formatValue(trade.rule_adherence)}/10</div>
          <div><strong>Impulsiveness:</strong> {formatValue(trade.impulsiveness)}/10</div>
          <div><strong>Energy Level:</strong> {formatValue(trade.energy_level)}/10</div>
          <div><strong>Market Context:</strong> {formatValue(trade.market_context)}/10</div>
          <div><strong>Outcome Satisfaction:</strong> {formatValue(trade.outcome_satisfaction)}/10</div>
          <div><strong>Pre-Trade Emotional State:</strong> {formatValue(trade.mental_state)}</div>
        </div>
      </section>

      {/* 2. EXECUTION */}
      <section style={{ marginBottom: 24 }}>
        <h3>2. Execution</h3>
        <div className="fos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13 }}>
          {isSessionShifted(trade) ? (
            <>
              <div>
                <strong>Session Date:</strong> {formatValue(trade.trade_date)}
                <span title="CME session day. Futures sessions run 6 PM ET prior day through 5 PM ET current day, so an evening trade belongs to the next session day." style={{ marginLeft: 4, opacity: 0.7 }}>🌙</span>
              </div>
              <div><strong>Entered:</strong> {formatValue(getEnteredCalendarDate(trade))} at {formatValue(trade.entry_time)}</div>
              <div><strong>Exit Time:</strong> {formatValue(trade.exit_time)}</div>
              <div><strong>Entry Price:</strong> {formatValue(trade.entry_price)}</div>
            </>
          ) : (
            <>
              <div><strong>Date:</strong> {formatValue(trade.trade_date)}</div>
              <div><strong>Entry Time:</strong> {formatValue(trade.entry_time)}</div>
              <div><strong>Exit Time:</strong> {formatValue(trade.exit_time)}</div>
              <div><strong>Entry Price:</strong> {formatValue(trade.entry_price)}</div>
            </>
          )}
          <div><strong>Exit Price:</strong> {formatValue(trade.exit_price)}</div>
          <div><strong>Stop Loss:</strong> {formatValue(trade.stop_loss)}</div>
          <div><strong>Take Profit:</strong> {formatValue(trade.take_profit)}</div>
          <div><strong>Position Size:</strong> {formatValue(trade.position_size)}</div>
          <div><strong>Setup Type:</strong> {formatValue(trade.setup_type)}</div>
          <div><strong>Duration:</strong> {formatValue(trade.duration_minutes)} min</div>
          <div><strong>Commissions:</strong> {formatValue(trade.commissions)}</div>
          <div><strong>Fees:</strong> {formatValue(trade.fees)}</div>
        </div>
      </section>

      {/* 3. POST-TRADE */}
      <section>
        <h3>3. Post-Trade Reflection</h3>
        <div className="fos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13 }}>
          <div><strong>Exit Reason:</strong> {formatValue(trade.exit_reason)}</div>
          <div><strong>Fear Level:</strong> {formatValue(trade.fear_level)}/10</div>
          <div><strong>Greed Level:</strong> {formatValue(trade.greed_level)}/10</div>
          <div><strong>Post-Trade Emotion:</strong> {formatValue(trade.post_trade_emotion)}</div>
          <div><strong>Outcome Satisfaction:</strong> {formatValue(trade.outcome_satisfaction)}/10</div>
        </div>

        <div style={{ marginTop: 16 }}>
          <strong>What did I learn from this trade?</strong>
          <p style={{ whiteSpace: "pre-wrap" }}>{formatValue(trade.post_trade_reflection)}</p>
        </div>

        <div style={{ marginTop: 16 }}>
          <strong>What would I do differently next time?</strong>
          <p style={{ whiteSpace: "pre-wrap" }}>{formatValue(trade.what_to_improve)}</p>
        </div>
      </section>

      <button 
        onClick={onEdit} 
        style={{width: "100%", padding: 16, background: "#4f8ef7", color: "white", border: "none", borderRadius: 8, marginTop: 28, fontWeight: 600}}
      >
        ✎ Edit Trade
      </button>
    </div>
  );
}

// =========================================================================
// FULL EDIT FORM - MATCHES ALL 3 STAGES
// =========================================================================
function TradeEditForm({ 
  editedForm, 
  setEditedForm, 
  onSave, 
  onCancel, 
  customStrategies = [],
  accounts = [] 
}) {
  if (!editedForm) return null;

  const builtInList = [
    { value: "Breakout_NewHigh", label: "Breakout - New High/Low" },
    { value: "Pullback_Support", label: "Pullback to Support/Resistance" },
    { value: "Reversal_Candle", label: "Reversal Candle (Pinbar, Engulfing)" },
    { value: "InsideBar_Breakout", label: "Inside Bar Breakout" },
    { value: "Flag_Pennant", label: "Bull/Bear Flag or Pennant" },
    { value: "Trend_Continuation", label: "Trend Continuation" },
    { value: "MA_Crossover", label: "Moving Average Crossover" },
    { value: "EMA_Ribbon", label: "EMA Ribbon Alignment" },
    { value: "Volume_Spike", label: "Volume Spike + Price Action" },
    { value: "RSI_Divergence", label: "RSI Divergence" },
    { value: "Bollinger_Squeeze", label: "Bollinger Band Squeeze" },
    { value: "VWAP_Reclaim", label: "VWAP Reclaim" },
    { value: "Scalp_Momentum", label: "Scalp - Momentum Ignition" },
    { value: "Opening_Range_Breakout", label: "Opening Range Breakout (ORB)" },
    { value: "News_Catalyst", label: "News / Economic Catalyst" },
    { value: "Confluence_Multiple", label: "Multiple Indicator Confluence" },
    { value: "OrderFlow_Delta", label: "Order Flow / Delta Divergence" },
    { value: "Mean_Reversion", label: "Mean Reversion Setup" },
  ];

  return (
    <div>
      <h3>Edit Trade</h3>

      {/* ACCOUNT */}
      {accounts.length > 0 && (
        <div style={{marginBottom: 20}}>
          <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>Account</label>
          <select 
            value={editedForm.account_id || ""} 
            onChange={e => setEditedForm({...editedForm, account_id: e.target.value})}
            style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#ffffff", border: `1px solid #1c1f30`, borderRadius: 8 }}
          >
            {accounts.map(acc => (
              <option key={acc.id} value={acc.id}>
                {acc.name} — {acc.account_type?.toUpperCase()} (${acc.starting_balance})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 1. PRE-TRADE */}
      <div style={{marginBottom: 28}}>
        <h4 style={{color: "#8a9ba8", marginBottom: 12}}>1. Pre-Trade</h4>
        <div className="fos-grid-2col" style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16}}>
          <div>
            <label>Market Condition</label>
            <select value={editedForm.market_condition || ''} onChange={e => setEditedForm({...editedForm, market_condition: e.target.value})}>
              <option value="">Select...</option>
              <option value="Trending Up">Trending Up</option>
              <option value="Trending Down">Trending Down</option>
              <option value="Ranging">Ranging</option>
              <option value="Volatile">Volatile</option>
            </select>
          </div>
          <div>
            <label>Entry Signal</label>
            <select value={editedForm.entry_signal || ''} onChange={e => setEditedForm({...editedForm, entry_signal: e.target.value})}>
              <option value="">Select...</option>
              {builtInList.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              {customStrategies.map(strat => <option key={strat.id} value={strat.name}>{strat.name}</option>)}
            </select>
          </div>
          <div style={{gridColumn: "1 / -1"}}>
            <label style={{ display: "block", marginBottom: 8, color: C.blue, fontWeight: 600 }}>
              Conviction Level: {editedForm.conviction_level || 5}/10
            </label>
            <input type="range" min="1" max="10" value={editedForm.conviction_level || 5} onChange={e => setEditedForm({...editedForm, conviction_level: +e.target.value})} style={{width: "100%", accentColor: C.blue}} />
          </div>
          <div style={{gridColumn: "1 / -1"}}>
            <label style={{ display: "block", marginBottom: 8, color: C.blue, fontWeight: 600 }}>
              Focus Level: {editedForm.focus_level || 5}/10
            </label>
            <input type="range" min="1" max="10" value={editedForm.focus_level || 5} onChange={e => setEditedForm({...editedForm, focus_level: +e.target.value})} style={{width: "100%", accentColor: C.blue}} />
          </div>
          <div style={{gridColumn: "1 / -1"}}>
            <label style={{ display: "block", marginBottom: 8, color: C.blue, fontWeight: 600 }}>
              Rule Adherence: {editedForm.rule_adherence || 5}/10
            </label>
            <input type="range" min="1" max="10" value={editedForm.rule_adherence || 5} onChange={e => setEditedForm({...editedForm, rule_adherence: +e.target.value})} style={{width: "100%", accentColor: C.blue}} />
          </div>
          <div style={{gridColumn: "1 / -1"}}>
            <label style={{ display: "block", marginBottom: 8, color: C.red, fontWeight: 600 }}>
              Impulsiveness: {editedForm.impulsiveness || 5}/10
            </label>
            <input type="range" min="1" max="10" value={editedForm.impulsiveness || 5} onChange={e => setEditedForm({...editedForm, impulsiveness: +e.target.value})} style={{width: "100%", accentColor: C.red}} />
          </div>
          <div style={{gridColumn: "1 / -1"}}>
            <label style={{ display: "block", marginBottom: 8, color: C.yellow, fontWeight: 600 }}>
              Energy Level: {editedForm.energy_level || 5}/10
            </label>
            <input type="range" min="1" max="10" value={editedForm.energy_level || 5} onChange={e => setEditedForm({...editedForm, energy_level: +e.target.value})} style={{width: "100%", accentColor: C.yellow}} />
          </div>
          <div style={{gridColumn: "1 / -1"}}>
            <label style={{ display: "block", marginBottom: 8, color: C.blue, fontWeight: 600 }}>
              Market Context: {editedForm.market_context || 5}/10
            </label>
            <input type="range" min="1" max="10" value={editedForm.market_context || 5} onChange={e => setEditedForm({...editedForm, market_context: +e.target.value})} style={{width: "100%", accentColor: C.blue}} />
          </div>
          <div style={{gridColumn: "1 / -1"}}>
            <label style={{ display: "block", marginBottom: 8, color: C.green, fontWeight: 600 }}>
              Outcome Satisfaction: {editedForm.outcome_satisfaction || 5}/10
            </label>
            <input type="range" min="1" max="10" value={editedForm.outcome_satisfaction || 5} onChange={e => setEditedForm({...editedForm, outcome_satisfaction: +e.target.value})} style={{width: "100%", accentColor: C.green}} />
          </div>
          <div>
            <label>Pre-Trade Emotional State</label>
            <select value={editedForm.mental_state || ''} onChange={e => setEditedForm({...editedForm, mental_state: e.target.value})}>
              <option value="">Select...</option>
              <option value="Calm">Calm & Focused</option>
              <option value="Anxious">Anxious / Hesitant</option>
              <option value="Excited">Excited / Eager</option>
              <option value="Frustrated">Frustrated / Tilted</option>
              <option value="Overconfident">Overconfident</option>
              <option value="Tired">Tired / Low Energy</option>
              <option value="FOMO">FOMO</option>
            </select>
          </div>
        </div>
      </div>

      {/* 2. EXECUTION */}
      <div style={{marginBottom: 28}}>
        <h4 style={{color: "#8a9ba8", marginBottom: 12}}>2. Execution</h4>
        <div className="fos-grid-2col" style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16}}>
          <div>
            <label>Entry Price</label>
            <input type="number" step="0.01" value={editedForm.entry_price || ''} onChange={e => setEditedForm({...editedForm, entry_price: e.target.value})} />
          </div>
          <div>
            <label>Exit Price</label>
            <input type="number" step="0.01" value={editedForm.exit_price || ''} onChange={e => setEditedForm({...editedForm, exit_price: e.target.value})} />
          </div>
          <div>
            <label>Stop Loss</label>
            <input type="number" step="0.01" value={editedForm.stop_loss || ''} onChange={e => setEditedForm({...editedForm, stop_loss: e.target.value})} />
          </div>
          <div>
            <label>Take Profit</label>
            <input type="number" step="0.01" value={editedForm.take_profit || ''} onChange={e => setEditedForm({...editedForm, take_profit: e.target.value})} />
          </div>
          <div>
            <label>Position Size</label>
            <input type="number" value={editedForm.position_size || ''} onChange={e => setEditedForm({...editedForm, position_size: e.target.value})} />
          </div>
          <div>
            <label>Setup Type</label>
            <select value={editedForm.setup_type || ''} onChange={e => setEditedForm({...editedForm, setup_type: e.target.value})}>
              <option value="">Select...</option>
              {builtInList.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              {customStrategies.map(strat => <option key={strat.id} value={strat.name}>{strat.name}</option>)}
            </select>
          </div>
          <div>
            <label>Entry Time</label>
            <input type="text" value={editedForm.entry_time || ''} onChange={e => setEditedForm({...editedForm, entry_time: e.target.value})} />
          </div>
          <div>
            <label>Exit Time</label>
            <input type="text" value={editedForm.exit_time || ''} onChange={e => setEditedForm({...editedForm, exit_time: e.target.value})} />
          </div>
          <div>
            <label>Gross P&L</label>
            <input type="number" step="0.01" value={editedForm.pnl ?? ''} onChange={e => setEditedForm({...editedForm, pnl: e.target.value})} />
          </div>
          <div>
            <label>Commissions</label>
            <input type="number" step="0.01" min="0" value={editedForm.commissions ?? ''} onChange={e => setEditedForm({...editedForm, commissions: e.target.value})} />
          </div>
          <div>
            <label>Fees</label>
            <input type="number" step="0.01" min="0" value={editedForm.fees ?? ''} onChange={e => setEditedForm({...editedForm, fees: e.target.value})} />
          </div>
        </div>
        <div style={{fontSize: 11, color: "#8a9ba8", marginTop: 10, lineHeight: 1.5}}>
          Net P&L = gross − commissions − fees ={" "}
          <strong style={{color: "#e0e0e0"}}>
            {(() => {
              const n = (Number(editedForm.pnl) || 0)
                      - (Number(editedForm.commissions) || 0)
                      - (Number(editedForm.fees) || 0);
              return `${n < 0 ? "−" : ""}$${Math.abs(n).toFixed(2)}`;
            })()}
          </strong>
          . Broker exports sometimes under-report fees — correct them here if your
          account balance disagrees.
        </div>
      </div>

      {/* 3. POST-TRADE */}
      <div>
        <h4 style={{color: "#8a9ba8", marginBottom: 12}}>3. Post-Trade Reflection</h4>
        <div className="fos-grid-2col" style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16}}>
          <div>
            <label>Exit Reason</label>
            <select value={editedForm.exit_reason || ''} onChange={e => setEditedForm({...editedForm, exit_reason: e.target.value})}>
              <option value="">Select...</option>
              <option value="target_hit">Target Hit</option>
              <option value="stop_hit">Stop Hit</option>
              <option value="manual_exit">Manual Exit</option>
              <option value="time_exit">Time Exit</option>
              <option value="breakeven">Breakeven</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div style={{gridColumn: "1 / -1"}}>
            <label style={{ display: "block", marginBottom: 8, color: C.red, fontWeight: 600 }}>
              Fear Level: {editedForm.fear_level || 5}/10
            </label>
            <input type="range" min="1" max="10" value={editedForm.fear_level || 5} onChange={e => setEditedForm({...editedForm, fear_level: +e.target.value})} style={{width: "100%", accentColor: C.red}} />
          </div>
          <div style={{gridColumn: "1 / -1"}}>
            <label style={{ display: "block", marginBottom: 8, color: C.green, fontWeight: 600 }}>
              Greed Level: {editedForm.greed_level || 5}/10
            </label>
            <input type="range" min="1" max="10" value={editedForm.greed_level || 5} onChange={e => setEditedForm({...editedForm, greed_level: +e.target.value})} style={{width: "100%", accentColor: C.green}} />
          </div>
          <div>
            <label>Post-Trade Emotion</label>
            <select value={editedForm.post_trade_emotion || ''} onChange={e => setEditedForm({...editedForm, post_trade_emotion: e.target.value})}>
              <option value="">Select...</option>
              <option value="Proud">Proud</option>
              <option value="Relieved">Relieved</option>
              <option value="Regretful">Regretful</option>
              <option value="Angry">Angry</option>
              <option value="Euphoric">Euphoric</option>
              <option value="Numb">Numb</option>
              <option value="Indifferent">Indifferent</option>
            </select>
          </div>
        </div>

        <div style={{marginTop: 16}}>
          <label>What did I learn from this trade?</label>
          <textarea rows="4" value={editedForm.post_trade_reflection || ''} onChange={e => setEditedForm({...editedForm, post_trade_reflection: e.target.value})} style={{width: "100%", padding: 12, background: "#1a1d2e", color: "#fff", borderRadius: 8}} />
        </div>

        <div style={{marginTop: 16}}>
          <label>What would I do differently next time?</label>
          <textarea rows="4" value={editedForm.what_to_improve || ''} onChange={e => setEditedForm({...editedForm, what_to_improve: e.target.value})} style={{width: "100%", padding: 12, background: "#1a1d2e", color: "#fff", borderRadius: 8}} />
        </div>
      </div>

      <div style={{marginTop: 32, display: "flex", gap: 12}}>
        <button onClick={onCancel} style={{flex: 1, padding: 14, background: "#333", color: "#ccc", border: "none", borderRadius: 8}}>Cancel</button>
        <button onClick={onSave} style={{flex: 1, padding: 14, background: "#4f8ef7", color: "white", border: "none", borderRadius: 8, fontWeight: 600}}>Save Changes</button>
      </div>
    </div>
  );
}

export default TradeHistoryModal;